import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import GlassCard from "../Cards/GlassCard";
import { DANGER, PRIMARY, SUCCESS, WARNING } from "../../utils/colors";
import axiosInstance from "../../services/axiosInstance";

const metricCardSx = (accentColor) => ({
  borderRadius: 3,
  border: "1px solid",
  borderColor: `${accentColor}55`,
  height: "100%",
});

const WEEK_OPTIONS = [4, 8, 12];

const LeetCodeSummaryCards = ({ batchId }) => {
  const [weeks, setWeeks] = useState(4);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [metrics, setMetrics] = useState({
    totalStudents: 0,
    activeStudents: 0,
    activePercentage: 0,
    inactiveStudents: 0,
    inactivePercentage: 0,
    avgProblemsPerActiveStudent: 0,
    engagementScore: 0,
    targetProblems: 20,
  });

  useEffect(() => {
    if (!batchId) return;
    let active = true;
    setLoading(true);
    setError("");

    axiosInstance
      .get("/analytics/leetcode/summary", { params: { batchId, weeks } })
      .then(({ data }) => {
        if (!active) return;
        setMetrics({
          totalStudents: data?.totalStudents || 0,
          activeStudents: data?.activeStudents || 0,
          activePercentage: data?.activePercentage || 0,
          inactiveStudents: data?.inactiveStudents || 0,
          inactivePercentage: data?.inactivePercentage || 0,
          avgProblemsPerActiveStudent: data?.avgProblemsPerActiveStudent || 0,
          engagementScore: data?.engagementScore || 0,
          targetProblems: data?.targetProblems || 20,
        });
      })
      .catch((err) => {
        if (!active) return;
        setError(err?.response?.data?.message || "Failed to load LeetCode summary.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [batchId, weeks]);

  if (loading) {
    return (
      <GlassCard hoverable={false} sx={{ borderRadius: 3, p: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
          <CircularProgress size={28} />
        </Box>
      </GlassCard>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ borderRadius: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 1.5, display: "flex", justifyContent: "flex-end" }}>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={weeks}
          onChange={(_, next) => {
            if (next) setWeeks(next);
          }}
          sx={{ "& .MuiToggleButton-root": { textTransform: "none", px: 1.25, fontWeight: 700 } }}
        >
          {WEEK_OPTIONS.map((w) => (
            <ToggleButton key={w} value={w}>
              Last {w} weeks
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>
      <Grid container spacing={2}>
      <Grid item xs={12} md={3}>
        <GlassCard hoverable={false} sx={metricCardSx(SUCCESS)}>
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
              Active Rate
            </Typography>
            <Typography variant="h5" fontWeight={800} sx={{ color: SUCCESS }}>
              {metrics.activePercentage}%
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {metrics.activeStudents} of {metrics.totalStudents} students active
            </Typography>
          </Box>
        </GlassCard>
      </Grid>

      <Grid item xs={12} md={3}>
        <GlassCard hoverable={false} sx={metricCardSx(PRIMARY)}>
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
              Avg Problems per Active Student
            </Typography>
            <Typography variant="h5" fontWeight={800} sx={{ color: PRIMARY }}>
              {metrics.avgProblemsPerActiveStudent}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Normalized by currently active students
            </Typography>
          </Box>
        </GlassCard>
      </Grid>

      <Grid item xs={12} md={3}>
        <GlassCard hoverable={false} sx={metricCardSx(WARNING)}>
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
              Engagement Score
            </Typography>
            <Typography variant="h5" fontWeight={800} sx={{ color: WARNING }}>
              {metrics.engagementScore}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              out of 100 (target {metrics.targetProblems} problems)
            </Typography>
          </Box>
        </GlassCard>
      </Grid>

      <Grid item xs={12} md={3}>
        <GlassCard
          hoverable={false}
          sx={metricCardSx(metrics.inactivePct > 20 ? DANGER : PRIMARY)}
        >
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
              Inactive Rate
            </Typography>
            <Typography
              variant="h5"
              fontWeight={800}
              sx={{ color: metrics.inactivePercentage > 20 ? DANGER : "text.primary" }}
            >
              {metrics.inactivePercentage}%
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: metrics.inactivePercentage > 20 ? DANGER : "text.secondary", fontWeight: 600 }}
            >
              {metrics.inactiveStudents} inactive students
            </Typography>
          </Box>
        </GlassCard>
      </Grid>
      </Grid>
    </Box>
  );
};

export default LeetCodeSummaryCards;
